import { DEFAULT_MODEL, DEFAULT_RATE, DEFAULT_BILLING } from '../../data/modalModelOptions'

export const TOTAL = 6 // steps 0..5 are input, 6 is done

export const initialReserveState = {
  isOpen: false,
  step: 0,
  flashMsg: null,
  answers: { model: [DEFAULT_MODEL], rate: DEFAULT_RATE, billing: DEFAULT_BILLING },
}

function spendBucketFor(spend) {
  if (spend < 10000) return 'Under $10K'
  if (spend < 50000) return '$10K-$50K'
  if (spend < 150000) return '$50K-$150K'
  return '$150K+'
}

// Mirrors validStep() from the original build, minus the DOM-reading side
// effects — controlled inputs already keep `answers` current as the user types.
export function validateStep(step, answers) {
  if (step === 0) {
    if (!answers.model || answers.model.length === 0) return 'Pick at least one model.'
    return null
  }
  if (step === 1) {
    if (!answers.company || !answers.name || !answers.email) return 'Please fill in company, name, and email.'
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(answers.email)) return 'That email doesn’t look right.'
    return null
  }
  if (step === 2) {
    if (!answers.segment) return 'Pick startup or enterprise.'
    return null
  }
  if (step === 3) {
    if (answers.segment === 'enterprise') {
      if (!answers.department) return 'Pick a department.'
      if (!answers.role) return 'Add your role or title.'
      if (!answers.industry) return 'Pick an industry.'
      if (!answers.orgTeamSize) return 'Pick a team size.'
    } else {
      if (!answers.stage) return 'Pick a funding stage.'
      if (!answers.team) return 'Pick a team size.'
    }
    return null
  }
  if (step === 4) {
    if (!answers.spend) return 'Pick your current spend range.'
    return null
  }
  if (step === 5) {
    if (!answers.timeline) return 'Pick a timeline.'
    return null
  }
  return null
}

export function reserveReducer(state, action) {
  switch (action.type) {
    case 'OPEN': {
      let answers = state.answers
      if (!answers.spend && action.reportedBurn) {
        answers = { ...answers, spend: spendBucketFor(action.reportedBurn) }
      }
      return { ...state, isOpen: true, step: 0, flashMsg: null, answers }
    }
    case 'CLOSE':
      return { ...state, isOpen: false }
    case 'SET_ANSWER':
      return { ...state, answers: { ...state.answers, [action.field]: action.value } }
    case 'ADVANCE': // +1 for the segment-card auto-advance — no-op if the user
      // already moved on (e.g. hit Continue before the 220ms timer fired), so a
      // late timer can't skip the details step. `from` is the step it was armed on.
      if (action.from !== undefined && state.step !== action.from) return state
      return { ...state, step: state.step + 1 }
    case 'NEXT': {
      const message = validateStep(state.step, state.answers)
      if (message) return { ...state, flashMsg: message }
      const nextStep = state.step === TOTAL - 1 ? TOTAL : state.step + 1
      return { ...state, step: nextStep, flashMsg: null }
    }
    case 'BACK':
      return state.step > 0 ? { ...state, step: state.step - 1 } : state
    case 'CLEAR_FLASH':
      return { ...state, flashMsg: null }
    default:
      return state
  }
}

// Reused by the final summary, the mailto submission body, and the Cal.com notes field.
export function burnLine(reportedBurn) {
  if (!reportedBurn) return null
  return 'Reported AI spend: $' + reportedBurn.toLocaleString('en-US') + '/month'
}

// `answers.model` is an array (multi-select) — everywhere it's rendered as
// text goes through this join, while the array itself stays the shape the
// StepModel picker works with.
function modelLine(answers) {
  return answers.model.join(', ')
}

export function buildPayload(answers, reportedBurn) {
  return {
    model: modelLine(answers), speedFloor: '500+', billing: answers.billing, flatRate: answers.rate,
    company: answers.company || '', contactName: answers.name || '', contactEmail: answers.email || '',
    segment: answers.segment || '',
    fundingStage: answers.stage || '', aiEngineers: answers.team || '',
    department: answers.department || '', roleTitle: answers.role || '', industry: answers.industry || '', orgTeamSize: answers.orgTeamSize || '',
    currentSpend: answers.spend || '', timeline: answers.timeline || '', workload: answers.use || '',
    reportedBurn: reportedBurn || null, agentsUsed: [],
  }
}

// Fire-and-forget POST — the server relays this to Slack (see web/server/reservations.js)
// and persists it. A network hiccup here can't block advancing to the Cal.com
// booking step, since it's not awaited.
export function postReservation(payload) {
  fetch('/api/reservations', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload) }).catch(() => {})
}

const esc = (s) => s.replace(/[<>&]/g, (c) => ({ '<': '&lt;', '>': '&gt;', '&': '&amp;' }[c]))

// This string is injected via dangerouslySetInnerHTML (StepTimeline.jsx), so
// every interpolated value must go through esc() — including the option-driven
// fields, so they stay safe if any is ever turned into a free-text input.
export function finalSummaryHtml(answers, reportedBurn) {
  const segLine = answers.segment === 'enterprise'
    ? 'Department: <b>' + esc(answers.department || '') + '</b> · Role: <b>' + esc(answers.role || '') + '</b><br>' +
      'Industry: <b>' + esc(answers.industry || '') + '</b> · Team size: <b>' + esc(answers.orgTeamSize || '') + '</b><br>'
    : 'Stage: <b>' + esc(answers.stage || '') + '</b> · Team: <b>' + esc(answers.team || '') + '</b><br>'
  const burn = burnLine(reportedBurn)
  return (
    'Company: <b>' + esc(answers.company || '') + '</b><br>' +
    segLine +
    (burn ? esc(burn) + '<br>' : '') +
    'Model: <b>' + esc(modelLine(answers)) + '</b><br>' +
    'Plan: <b>' + esc(answers.rate || '') + '</b>'
  )
}

export function calNotes(answers, reportedBurn) {
  const segLine = answers.segment === 'enterprise'
    ? 'Department: ' + (answers.department || '') + ' · Role: ' + (answers.role || '') + ' · Industry: ' + (answers.industry || '') + ' · Team size: ' + (answers.orgTeamSize || '')
    : 'Funding stage: ' + (answers.stage || '') + ' · AI engineers: ' + (answers.team || '')
  return [
    'Company: ' + (answers.company || ''),
    'Segment: ' + (answers.segment === 'enterprise' ? 'Enterprise' : 'Startup'),
    segLine,
    'Current monthly AI spend: ' + (answers.spend || ''),
    burnLine(reportedBurn),
    'Model: ' + modelLine(answers) + ' · Plan: ' + answers.rate,
    answers.use ? 'Workload: ' + answers.use : null,
  ].filter(Boolean).join('\n')
}
